import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Camera, X, Loader2, Plus, RefreshCw, Flame, Beef, Wheat, Droplet, Scale, Edit3, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { MealTimeSelector } from '../components/MealTimeSelector';

export function FoodPhotoModal({
  isOpen,
  onClose,
  onAddFood,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [foodName, setFoodName] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [servingSize, setServingSize] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);
  const [editingName, setEditingName] = useState(false);
  const [editedFoodName, setEditedFoodName] = useState('');

  // Auto-fill serving size from AI estimate
  useEffect(() => {
    if (result?.estimated_weight_grams) {
      setServingSize(String(result.estimated_weight_grams));
    }
  }, [result]);

  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsScanning(true);
    try {
      const base64 = await compressImage(file);
      const response = await api.post('/nutrition/scan-food-photo', {
        image_data: base64,
        food_name: foodName || null
      });
      setResult(response);
      toast.success(`Identified: ${response.food_name} (~${response.estimated_weight_grams || '?'}g)`);
    } catch (err) {
      console.error('Food photo scan error:', err);
      toast.error('Failed to analyze food: ' + (err.message || 'Unknown error'));
    } finally {
      setIsScanning(false);
    }
  };

  const portion = parseInt(servingSize) || 100;
  const multiplier = portion / 100;

  const handleAdd = () => {
    if (!result) return;
    onAddFood({
      name: result.food_name,
      calories: Math.round((result.calories || 0) * multiplier),
      protein: Math.round((result.protein || 0) * multiplier * 10) / 10,
      carbs: Math.round((result.carbs || 0) * multiplier * 10) / 10,
      fat: Math.round((result.fat || 0) * multiplier * 10) / 10,
      grams: portion,
      isPhotoScan: true,
      mealTime: selectedMealTime,
    });
    handleClose();
  };

  const handleClose = () => {
    setFoodName('');
    setResult(null);
    setServingSize('');
    setEditingName(false);
    setEditedFoodName('');
    onClose();
  };

  const resetScan = () => {
    setResult(null);
    setServingSize('');
    setEditingName(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <Camera className="h-5 w-5 text-amber-400" />
            Food Photo Scanner
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="overflow-y-auto flex-1 space-y-4">
          {!result ? (
            <>
              <div>
                <Label className="text-white">Food Name (optional)</Label>
                <Input
                  value={foodName}
                  onChange={(e) => setFoodName(e.target.value)}
                  placeholder="e.g., Birthday cake, Pasta carbonara"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">AI will identify the food and correct the name if needed</p>
              </div>

              <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  id="food-photo-input"
                  onChange={handleFileSelect}
                  data-testid="food-photo-input"
                />
                <label htmlFor="food-photo-input" className="cursor-pointer">
                  {isScanning ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-12 w-12 text-amber-400 animate-spin mb-2" />
                      <p className="text-white font-medium">Analyzing food...</p>
                      <p className="text-slate-400 text-sm">AI is identifying food & estimating weight</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Camera className="h-14 w-14 text-amber-400 mb-3" />
                      <p className="text-white font-medium">Take Photo of Food</p>
                      <p className="text-slate-400 text-sm mt-1">AI will identify food, estimate weight & nutrition</p>
                    </div>
                  )}
                </label>
              </div>
            </>
          ) : (
            <>
              {/* AI Result */}
              <div className="bg-slate-700/50 rounded-xl p-4 space-y-4">
                {/* Food Name - editable */}
                <div className="flex items-center justify-between">
                  {editingName ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editedFoodName}
                        onChange={(e) => setEditedFoodName(e.target.value)}
                        className="bg-slate-600 border-slate-500 text-white h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && editedFoodName.trim()) {
                            setResult(r => ({ ...r, food_name: editedFoodName.trim() }));
                            setEditingName(false);
                          }
                        }}
                      />
                      <button onClick={() => {
                        if (editedFoodName.trim()) setResult(r => ({ ...r, food_name: editedFoodName.trim() }));
                        setEditingName(false);
                      }} className="text-green-400 p-1">
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold text-lg" data-testid="food-photo-name">{result.food_name}</h3>
                      <button onClick={() => { setEditedFoodName(result.food_name); setEditingName(true); }}
                        className="text-amber-400 hover:text-amber-300 p-1" data-testid="edit-food-photo-name">
                        <Edit3 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" onClick={resetScan} className="text-slate-400 hover:text-white ml-2">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>

                {/* AI description */}
                {result.description && (
                  <p className="text-slate-400 text-xs">{result.description}</p>
                )}

                {/* AI Estimated Weight - prominent display */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-400 text-sm font-medium">AI Estimated Weight</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={servingSize}
                      onChange={(e) => setServingSize(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white w-24 h-9 text-center font-bold"
                      data-testid="serving-size-input"
                    />
                    <span className="text-slate-300 text-sm">grams</span>
                    {result.estimated_weight_grams && parseInt(servingSize) !== result.estimated_weight_grams && (
                      <button
                        onClick={() => setServingSize(String(result.estimated_weight_grams))}
                        className="text-xs text-amber-400 hover:text-amber-300 underline"
                      >
                        Reset to {result.estimated_weight_grams}g
                      </button>
                    )}
                  </div>
                  {/* Quick adjust buttons */}
                  <div className="flex gap-1 mt-2">
                    {[-50, -25, 25, 50].map(delta => (
                      <button
                        key={delta}
                        onClick={() => setServingSize(String(Math.max(10, portion + delta)))}
                        className="px-2 py-1 rounded text-xs bg-slate-700 text-slate-300 hover:bg-slate-600"
                      >
                        {delta > 0 ? '+' : ''}{delta}g
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Calculated Nutrition */}
                <div>
                  <p className="text-slate-400 text-xs mb-2">Nutrition for {portion}g:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-orange-400">
                      <Flame className="h-4 w-4" />
                      <span className="font-medium">{Math.round((result.calories || 0) * multiplier)} cal</span>
                    </div>
                    <div className="flex items-center gap-2 text-red-400">
                      <Beef className="h-4 w-4" />
                      <span>{Math.round((result.protein || 0) * multiplier * 10) / 10}g protein</span>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-400">
                      <Wheat className="h-4 w-4" />
                      <span>{Math.round((result.carbs || 0) * multiplier * 10) / 10}g carbs</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-400">
                      <Droplet className="h-4 w-4" />
                      <span>{Math.round((result.fat || 0) * multiplier * 10) / 10}g fat</span>
                    </div>
                  </div>
                </div>
                
                {result.confidence != null && (
                  <p className="text-xs text-slate-500">
                    AI Confidence: {Math.round((typeof result.confidence === 'number' ? result.confidence : 0.5) * 100)}%
                  </p>
                )}
              </div>

              {/* Meal Time Selection */}
              <div className="space-y-1">
                <label className="text-sm text-slate-400">Add to:</label>
                <MealTimeSelector 
                  selectedMealTime={selectedMealTime} 
                  onMealTimeChange={setSelectedMealTime}
                  compact
                  mealTimes={mealTimes}
                />
              </div>
              
              <Button onClick={handleAdd} className="w-full bg-green-600 hover:bg-green-500" data-testid="add-food-photo-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add {result.food_name} ({portion}g)
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FoodPhotoModal;
